import { useState } from 'react';
import axios from 'axios';
import { Check, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { USERNAME_MAX_LENGTH, USERNAME_MIN_LENGTH, validateUsernameClient } from '@/lib/username-rules';
import { getUsersGetMeQueryKey, useUsersUpdateMe } from '@/lib/endpoints';

interface InlineUsernameEditorProps {
  /** Current saved username, seeds the input. */
  initialValue: string;
  /** Called when editing finishes (saved or cancelled). */
  onDone: () => void;
}

/** Map a PATCH /users/me failure to the inline message the player sees. */
function toErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 409) return 'That username is already taken.';
    if (err.response?.status === 400) {
      const message = (err.response.data as { message?: string | string[] })?.message;
      const reason = Array.isArray(message) ? message[0] : message;
      return reason ?? 'That username is not allowed.';
    }
  }
  return 'Could not save. Please try again.';
}

/**
 * In-place username editor shown when the player taps the pencil in the
 * settings account header. Runs the same shape rules the server enforces
 * for fast feedback and surfaces 400/409 responses inline. Enter saves,
 * Escape cancels; an unchanged value just closes the editor.
 */
export function InlineUsernameEditor({ initialValue, onDone }: InlineUsernameEditorProps) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(initialValue);
  const [serverError, setServerError] = useState('');

  const mutation = useUsersUpdateMe({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getUsersGetMeQueryKey() });
        onDone();
      },
      onError: (err) => setServerError(toErrorMessage(err)),
    },
  });

  const trimmed = value.trim();
  const clientError = trimmed === initialValue ? null : validateUsernameClient(value);
  const error = serverError || clientError;

  function save() {
    if (mutation.isPending) return;
    if (trimmed === initialValue) {
      onDone();
      return;
    }
    if (validateUsernameClient(value)) return;
    setServerError('');
    mutation.mutate({ data: { username: trimmed } });
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <input
          // The editor appears on the player's explicit pencil tap, so
          // grabbing focus is the expected behavior, not a focus steal.
          autoFocus
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (serverError) setServerError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') onDone();
          }}
          aria-label="Username"
          aria-invalid={Boolean(error)}
          minLength={USERNAME_MIN_LENGTH}
          maxLength={USERNAME_MAX_LENGTH}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          className={cn(
            // Figma "Name input": pill with the subtle stroke even while focused —
            // the border + text caret carry the focus affordance, no accent ring.
            'h-8 w-full min-w-0 max-w-[240px] rounded-pill border-[1.5px] bg-surface px-[18px] text-base text-ink',
            'focus:outline-none',
            error ? 'border-danger-alt' : 'border-stroke-soft',
          )}
        />
        <button
          type="button"
          onClick={save}
          disabled={mutation.isPending || Boolean(clientError)}
          aria-label="Save username"
          className="shrink-0 rounded-full p-1.5 text-purple transition-opacity hover:opacity-80 disabled:opacity-40"
        >
          <Check aria-hidden="true" className="size-5" />
        </button>
        <button
          type="button"
          onClick={onDone}
          disabled={mutation.isPending}
          aria-label="Cancel editing"
          className="shrink-0 rounded-full p-1.5 text-muted transition-opacity hover:opacity-80 disabled:opacity-40"
        >
          <X aria-hidden="true" className="size-5" />
        </button>
      </div>
      {error && <p className="text-sm text-danger-alt">{error}</p>}
    </div>
  );
}
